import { Search } from "lucide-react-native";
import { memo } from "react";

import type { KanbanColors } from "@/features/player-kanban/constants/colors";

// design-system: compound waiver — non-collapsible input composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: Input carries data-driven theme colors (inputBoxStyle/inputTextStyle) + InputIcon color={colors.textSecondary} +…
import { Input, InputField, InputIcon, InputSlot } from "@/components/ui/input";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";

/*
 * design-system: dynamic-style waiver — data-driven theme colours (search card
 * background/border/text) that no semantic token expresses here; passed as
 * single style references so no inline object literal renders on a JSX element.
 */
const inputBoxStyle = (cardBackground: string, border: string) => ({
  backgroundColor: cardBackground,
  borderWidth: 1,
  borderColor: border,
});
const inputTextStyle = (color: string) => ({ color, fontSize: 13 });

/**
 * Props for SearchAndControlsView component
 */
interface SearchAndControlsViewProps {
  searchQuery: string;
  sortLabel: string;
  activeFilterCount?: number;
  showFilterDropdown: boolean;
  showSortDropdown: boolean;
  colors: KanbanColors;
  activeFilters?: {
    positions: string[];
    ageRange: [number, number];
    gprRange: [number, number];
  };
  onSearchChange: (query: string) => void;
  onSortChange: () => void;
  onFilterPress?: () => void;
  onTogglePosition?: (position: string) => void;
  onClearFilters?: () => void;
  onCloseAllDropdowns: () => void;
  onFilterDropdownToggle: () => void;
  onSortDropdownToggle: () => void;
}

/**
 * View component for SearchAndControls - renders search bar with filter and sort controls
 * @param root0 - The component props
 * @param root0.searchQuery - Current search query string for filtering players
 * @param root0.sortLabel - Label describing the current sort configuration
 * @param root0.activeFilterCount - Number of active filters currently applied (optional)
 * @param root0.showFilterDropdown - Whether the filter dropdown is currently visible
 * @param root0.showSortDropdown - Whether the sort dropdown is currently visible
 * @param root0.activeFilters - Currently active filter settings including positions, age range, and GPR range (optional)
 * @param root0.onSearchChange - Callback invoked when the search query changes
 * @param root0.onSortChange - Callback invoked to trigger sort changes
 * @param root0.onFilterPress - Callback invoked when the filter button is pressed (optional)
 * @param root0.onTogglePosition - Callback to toggle a position filter on/off (optional)
 * @param root0.onClearFilters - Callback to clear all active filters (optional)
 * @param root0.onCloseAllDropdowns - Callback to close all open dropdown menus
 * @param root0.onFilterDropdownToggle - Callback to toggle the filter dropdown visibility
 * @param root0.onSortDropdownToggle - Callback to toggle the sort dropdown visibility
 * @param root0.colors - Theme colors for styling the component
 */
const SearchAndControlsView = ({
  searchQuery,
  sortLabel: _sortLabel,
  activeFilterCount: _activeFilterCount = 0,
  showFilterDropdown,
  showSortDropdown,
  colors,
  activeFilters: _activeFilters,
  onSearchChange,
  onSortChange: _onSortChange,
  onFilterPress: _onFilterPress,
  onTogglePosition: _onTogglePosition,
  onClearFilters: _onClearFilters,
  onCloseAllDropdowns,
  onFilterDropdownToggle: _onFilterDropdownToggle,
  onSortDropdownToggle: _onSortDropdownToggle,
}: SearchAndControlsViewProps) => (
  <>
    {/* Backdrop overlay */}
    {(showFilterDropdown || showSortDropdown) && (
      <Pressable
        onPress={onCloseAllDropdowns}
        className="absolute inset-0 z-40 bg-transparent"
      />
    )}

    <Stack className="z-50 overflow-visible px-3 pb-2">
      {/* Compact Search Bar */}
      <Input
        className="rounded-sm"
        // eslint-disable-next-line no-restricted-syntax -- data-driven KanbanColors card background/border (arbitrary per-theme hex); raw ui Input kept because leading InputIcon needs a data-driven color the atom's closed tone enum can't carry
        style={inputBoxStyle(colors.cardBackground, colors.border)}
      >
        <InputSlot className="pl-2">
          <InputIcon as={Search} size="sm" color={colors.textSecondary} />
        </InputSlot>
        <InputField
          placeholder="Search players..."
          value={searchQuery}
          onChangeText={onSearchChange}
          className="py-2 pl-2"
          // eslint-disable-next-line no-restricted-syntax -- data-driven KanbanColors text color + fontSize 13 (off the text-variant ladder); part of the same non-collapsible ui Input composition
          style={inputTextStyle(colors.textPrimary)}
          placeholderTextColor={colors.textMuted}
        />
      </Input>
    </Stack>
  </>
);

SearchAndControlsView.displayName = "SearchAndControlsView";

export default memo(SearchAndControlsView);
